
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, Package, Trash2, Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAppCatalogSearch } from "@/hooks/useAppCatalogSearch";
import { useMutation } from "convex/react";
import { api } from "../../api";
import { AppIcon } from "./AppIcon";
import { useAppCache } from "@/hooks/useAppCache";

interface LevelAppsBlacklistContentProps {
  levelId: string;
  blockedApps?: string[];
  allowedApps?: string[];
}

export const LevelAppsBlacklistContent = ({ levelId, blockedApps, allowedApps }: LevelAppsBlacklistContentProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();
  const { getApp } = useAppCache();

  // Use the app catalog search hook
  const { data: searchResults, isLoading: isSearching } = useAppCatalogSearch(searchQuery);
  
  // Convex mutation for updating level apps
  const updateLevelApps = useMutation(api.levels.apps.update);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied",
        description: "Error message copied to clipboard",
      });
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleAddToBlacklist = async (app: any) => {
    try {
      console.log('Adding app to blacklist:', app, 'for level:', levelId);
      console.log('Current blocked apps:', blockedApps);
      
      // Get current blocked apps and add the new one using the app ID, not bundle ID
      const currentBlockedApps = blockedApps || [];
      const newBlockedApps = [...currentBlockedApps, app.id]; // Using app.id instead of app.bundleId
      
      console.log('New blocked apps array:', newBlockedApps);
      
      const result = await updateLevelApps({
        level: levelId as any,
        blockedApps: newBlockedApps as any[]
      });
      
      console.log('Update result:', result);
      
      toast({
        title: "App Blocked",
        description: `${app.appName} has been added to the blacklist.`,
      });
    } catch (error) {
      console.error('Error adding app to blacklist:', error);
      console.error('Error details:', {
        levelId,
        appId: app.id,
        appName: app.appName,
        currentBlockedApps: blockedApps
      });
      
      const errorMessage = `Failed to add app to blacklist: ${error?.message || 'Unknown error'}`;
      
      toast({
        title: "Error",
        description: (
          <div className="flex items-center justify-between gap-2">
            <span className="flex-1">{errorMessage}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copyToClipboard(errorMessage)}
              className="h-6 w-6 p-0"
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        ),
        variant: "destructive",
      });
    }
  };

  const handleRemoveFromBlacklist = async (appId: string) => {
    try {
      console.log('Removing app from blacklist:', appId, 'for level:', levelId);
      
      // Remove the app from blocked apps
      const currentBlockedApps = blockedApps || [];
      const newBlockedApps = currentBlockedApps.filter(id => id !== appId);
      
      await updateLevelApps({
        level: levelId as any,
        blockedApps: newBlockedApps as any[]
      });
      
      toast({
        title: "App Unblocked",
        description: "App has been removed from the blacklist.",
      });
    } catch (error) {
      console.error('Error removing app from blacklist:', error);
      
      const errorMessage = "Failed to remove app from blacklist. Please try again.";
      
      toast({
        title: "Error",
        description: (
          <div className="flex items-center justify-between gap-2">
            <span className="flex-1">{errorMessage}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copyToClipboard(errorMessage)}
              className="h-6 w-6 p-0"
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        ),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">Blacklist</h3>
        <p className="text-gray-600">Apps that are explicitly blocked for this level</p>
      </div>

      {/* Search Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add Apps to Blacklist</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search for apps to block..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Search Results */}
          {searchQuery && (
            <div className="space-y-2">
              {isSearching ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : searchResults && searchResults.length > 0 ? (
                <>
                  <h4 className="font-medium text-sm">Search Results</h4>
                  {searchResults
                    .filter(app => !blockedApps?.includes(app.id)) // Filter by app.id instead of app.bundleId
                    .map((app) => (
                    <div key={app.bundleId} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <AppIcon app_icon_url={app.iconUrl} app_name={app.appName} />
                        <div>
                          <p className="font-medium text-sm">{app.appName}</p>
                          <p className="text-xs text-gray-500">{app.bundleId}</p>
                        </div>
                      </div>
                      <Button size="sm" onClick={() => handleAddToBlacklist(app)}>
                        <Plus className="w-4 h-4 mr-1" />
                        Block
                      </Button>
                    </div>
                  ))}
                </>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-500">No apps found for "{searchQuery}"</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Current Blacklist */}
      <div>
        <h4 className="font-medium mb-3">Current Blacklist</h4>
        {!blockedApps || blockedApps.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <Package className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">No apps in blacklist</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {blockedApps.map((appId, index) => {
              const appData = getApp(appId);
              return (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <AppIcon app_icon_url={appData.iconUrl} app_name={appData.name} />
                    <div>
                      <p className="font-medium text-sm">{appData.name}</p>
                      <p className="text-xs text-gray-500">{appId}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive">Blocked</Badge>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleRemoveFromBlacklist(appId)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
